/* BitmovinDrmDetectorPlugin
 * Detects supported DRM systems in the current browser environment
 * and helps build a Bitmovin Player source configuration with DRM.
 * * */

class DrmDetector {
  constructor(videoTypes, timeoutMs) {
    this.videoTypes = videoTypes || ['video/mp4; codecs="avc1.42E01E"']; // H.264 baseline
    this.timeoutMs = timeoutMs || 2500;
    this.cache = null;
  }

  async probe(keySystem) {
    const fallback = { supported: false, keySystem, videoCapabilities: [], error: null };
    if (!('requestMediaKeySystemAccess' in navigator)) return fallback;

    const configs = [{
      initDataTypes: ['cenc', 'keyids'],
      persistentState: 'optional',
      distinctiveIdentifier: 'optional',
      videoCapabilities: this.videoTypes.map(t => ({ contentType: t })),
      audioCapabilities: [{ contentType: 'audio/mp4; codecs="mp4a.40.2"' }],
    }];

    // Simple timeout wrapper
    const withTimeout = (p, ms) =>
      new Promise((resolve) => {
        let done = false;
        const t = setTimeout(() => {
          if (!done) { done = true; resolve({ timeout: true }); }
        }, ms);
        p.then(v => { if (!done) { done = true; clearTimeout(t); resolve(v); } })
         .catch(e => { if (!done) { done = true; clearTimeout(t); resolve({ error: e }); } });
      });

    const attempt = await withTimeout(
      navigator.requestMediaKeySystemAccess(keySystem, configs),
      this.timeoutMs
    );

    if (attempt?.timeout) return fallback;
    if (attempt?.error) return { ...fallback, error: attempt.error };

    try {
      const access = attempt;
      const cfg = access.getConfiguration();
      return {
        supported: true,
        keySystem,
        videoCapabilities: (cfg.videoCapabilities || []).map(v => v.contentType || '')
      };
    } catch (e) {
      return { ...fallback, error: e };
    }
  }

  async detectAll() {
    if (this.cache) return this.cache;

    const [wv, pr, fp] = await Promise.all([
      this.probe('com.widevine.alpha'),
      this.probe('com.microsoft.playready'),
      this.probe('com.apple.fps.1_0'),
    ]);

    this.cache = {
      widevine: !!wv.supported,
      playready: !!pr.supported,
      fairplay: !!fp.supported,
      details: { widevine: wv, playready: pr, fairplay: fp }
    };
    return this.cache;
  }

  async supports(name) {
    const r = await this.detectAll();
    return !!r[name];
  }
}

export default DrmDetector;